// =============================================================================
// Appointment detail — faithful port of IMS Appointment_View / Appointment_Add.
// Shows customer block + appointment details (made date, date/time, designer,
// pages, comments, go-ahead, outcome, on-the-spot, advise, look-at-job/plants).
// =============================================================================
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, XCircle, Pencil, Image as ImageIcon, FileText } from 'lucide-react'
import { getAppointment } from '../services/data'
import { PageHeader, Card, Spinner, Button } from '../components/shared/ui'
import { customerLabel, date } from '../lib/format'
import AppointmentForm from '../components/AppointmentForm'
import CustomerMediaPanel from '../components/CustomerMediaPanel'
import type { Appointment, Customer } from '../types'

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === '') return null
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-gray-50 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-800 text-right">{value}</span>
    </div>
  )
}

export default function AppointmentDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [appt, setAppt] = useState<Appointment | null>(null)
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [tab, setTab] = useState<'details' | 'media'>('details')

  const load = () => {
    if (!id) return
    setLoading(true)
    getAppointment(id)
      .then(res => { if (res) { setAppt(res.appointment); setCustomer(res.customer) } })
      .finally(() => setLoading(false))
  }
  useEffect(load, [id])

  if (loading) return <Spinner label="Loading appointment…" />
  if (!appt) return <div className="text-gray-500">Appointment not found.</div>

  const apptTime = appt.appointment_time
    ? new Date(appt.appointment_time).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : null

  return (
    <div>
      <Button variant="ghost" onClick={() => navigate('/appointments')} className="mb-4">
        <ArrowLeft size={16} /> Back to Appointments
      </Button>
      <PageHeader
        title={`Appointment — ${date(appt.appointment_date)}`}
        subtitle={apptTime ?? undefined}
        actions={
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${appt.go_ahead ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
              {appt.go_ahead ? <CheckCircle2 size={15} /> : <XCircle size={15} />} {appt.go_ahead ? 'Go Ahead' : 'No Go-Ahead'}
            </span>
            <Button variant="secondary" onClick={() => setEditing(true)}><Pencil size={16} /> Edit</Button>
          </div>
        }
      />

      {editing && (
        <AppointmentForm
          appointment={appt}
          customer={customer}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load() }}
        />
      )}

      <div className="flex gap-1 border-b border-gray-200 mb-6">
        <button onClick={() => setTab('details')}
          className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'details' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          <FileText size={15} /> Details
        </button>
        <button onClick={() => setTab('media')}
          className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'media' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          <ImageIcon size={15} /> Media
        </button>
      </div>

      {tab === 'media' ? (
        <Card className="p-5">
          <h3 className="font-semibold text-gray-800 mb-1">Site Visit Media</h3>
          <p className="text-sm text-gray-500 mb-4">Photos, videos, and documents for this customer. Uploads here are tagged to this appointment and can be recalled when building an estimate.</p>
          {customer
            ? <CustomerMediaPanel customerId={customer.id} appointmentId={appt.id} />
            : <p className="text-sm text-gray-400">Link a customer to this appointment to add media.</p>}
        </Card>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-semibold text-gray-800 mb-3">Customer</h3>
          {customer ? (
            <div className="text-sm space-y-1 text-gray-700">
              <p className="font-medium">{customerLabel(customer)}</p>
              {customer.company_name && <p className="text-gray-500">{customer.company_name}</p>}
              <p>{[customer.address, customer.city, customer.state, customer.zip].filter(Boolean).join(', ')}</p>
              {customer.phone_home && <p>H: {customer.phone_home}</p>}
              {customer.phone_work && <p>W: {customer.phone_work}</p>}
              {customer.email && <p>{customer.email}</p>}
              <button onClick={() => navigate(`/customers/${customer.id}`)} className="text-green-700 hover:underline text-xs mt-1">View customer →</button>
            </div>
          ) : <p className="text-sm text-gray-400">No customer linked</p>}
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-gray-800 mb-3">Appointment Details</h3>
          <div>
            <Row label="Appointment Made" value={appt.appointment_made_date} />
            <Row label="Date" value={date(appt.appointment_date)} />
            <Row label="Time" value={apptTime} />
            <Row label="Designer" value={appt.designer_name} />
            <Row label="Pages" value={appt.pages} />
            <Row label="Outcome" value={appt.outcome} />
            <Row label="On The Spot" value={appt.on_the_spot} />
            <Row label="Advise" value={appt.advise} />
            <Row label="Look At Job" value={appt.look_at_job} />
            <Row label="Look At Plants" value={appt.look_at_plants} />
            <Row label="Mailed Date" value={appt.mailed_date} />
          </div>
          {appt.comments && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Comments</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{appt.comments}</p>
            </div>
          )}
        </Card>
      </div>
      )}
    </div>
  )
}
